import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/FacebookPageAnalyticsAudience.graphql';
import { Query, QueryfacebookPageAnalyticsAudienceArgs } from '../../__generated__/globalTypes';

export const useFacebookPageAnalyticsAudienceQuery = (
  options?: QueryHookOptions<Query, QueryfacebookPageAnalyticsAudienceArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryfacebookPageAnalyticsAudienceArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useFacebookPageAnalyticsAudienceLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryfacebookPageAnalyticsAudienceArgs>
): QueryResult<Query, QueryfacebookPageAnalyticsAudienceArgs> & {
  getFacebookPageAnalyticsAudience: LazyQueryExecFunction<Query, QueryfacebookPageAnalyticsAudienceArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getFacebookPageAnalyticsAudience, result] = useLazyQuery<Query, QueryfacebookPageAnalyticsAudienceArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getFacebookPageAnalyticsAudience, ...result };
};

export const useFacebookPageAnalyticsAudiencePromiseQuery = () => {
  const client = useApolloClient();

  const getFacebookPageAnalyticsAudience = useCallback(
    (options?: Omit<QueryOptions<QueryfacebookPageAnalyticsAudienceArgs, Query>, 'query'>) =>
      client.query<Query, QueryfacebookPageAnalyticsAudienceArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getFacebookPageAnalyticsAudience };
};
// Suspense Query Hooks
export const useFacebookPageAnalyticsAudienceSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryfacebookPageAnalyticsAudienceArgs>
) => {
  const result = useSuspenseQuery<Query, QueryfacebookPageAnalyticsAudienceArgs>(QUERY, {
    ...options
  });

  return result;
};
